/**
 * Script Context
 *
 * Proxy layer mapping the portable Script API to Babylon.js internals.
 * Creates ScriptContext objects that get passed into every lifecycle hook.
 */

import type { SceneObject } from '../core/SceneObject';
import type { TraceScene } from '../core/TraceScene';
import type { BehaviorData } from '../scenedata/behaviordata';
import type {
    Vec3,
    ScriptContext,
    SceneObjectHandle,
    SceneHandle,
    BehaviorHandle,
} from './script-api-types';
import { Properties } from '../behaviors/properties';
import { Logger } from '../utils/Logger';
import { Animation } from '../behaviors/animation';
import { Renderer } from '../behaviors/renderer';
import { Synth } from '../behaviors/synth';

import type { ScriptBehavior } from './ScriptBehavior';

const RAD_TO_DEG = 180 / Math.PI;
const DEG_TO_RAD = Math.PI / 180;

// ── SceneObjectHandle implementation ─────────────────────

class SceneObjectHandleImpl implements SceneObjectHandle {
    constructor(
        private _sceneObject: SceneObject,
        private _traceScene: TraceScene,
        /** @type of the calling script — dispatch() skips this behavior to prevent self-firing. */
        private _callerType?: string,
    ) {}

    get name(): string {
        return this._sceneObject.name;
    }

    get path(): string {
        return this._sceneObject.getPath();
    }

    get active(): boolean {
        return this._sceneObject.transform.isEnabled();
    }

    set active(v: boolean) {
        this._sceneObject.transform.setEnabled(v);
    }

    // ── Transform (position/scale pass through; rotation converts rad↔deg) ──

    get position(): Vec3 {
        const p = this._sceneObject.transform.position;
        return { x: p.x, y: p.y, z: p.z };
    }

    set position(v: Vec3) {
        this._sceneObject.transform.position.set(v.x, v.y, v.z);
    }

    get rotation(): Vec3 {
        // A node in quaternion mode (as any TransformCommand undo leaves one)
        // holds a stale Euler `rotation`; its quaternion is the rotation it has.
        const t = this._sceneObject.transform;
        const r = t.rotationQuaternion?.toEulerAngles() ?? t.rotation;
        return {
            x: r.x * RAD_TO_DEG,
            y: r.y * RAD_TO_DEG,
            z: r.z * RAD_TO_DEG,
        };
    }

    set rotation(v: Vec3) {
        this._sceneObject.transform.rotation.set(
            v.x * DEG_TO_RAD,
            v.y * DEG_TO_RAD,
            v.z * DEG_TO_RAD,
        );
        // Clear quaternion so Euler angles take effect
        this._sceneObject.transform.rotationQuaternion = null;
    }

    get scale(): Vec3 {
        const s = this._sceneObject.transform.scaling;
        return { x: s.x, y: s.y, z: s.z };
    }

    set scale(v: Vec3) {
        this._sceneObject.transform.scaling.set(v.x, v.y, v.z);
    }

    get worldPosition(): Vec3 {
        const wp = this._sceneObject.transform.getAbsolutePosition();
        return { x: wp.x, y: wp.y, z: wp.z };
    }

    // ── Hierarchy ────────────────────────────────────────

    getParent(): SceneObjectHandle | null {
        const parent = this._sceneObject.getParent();
        return parent ? new SceneObjectHandleImpl(parent, this._traceScene) : null;
    }

    getChildren(): SceneObjectHandle[] {
        return this._sceneObject.getChildren()
            .map(child => new SceneObjectHandleImpl(child, this._traceScene));
    }

    findChild(name: string): SceneObjectHandle | null {
        const resolved = this._traceScene.resolveSceneObject(`./${name}`, this._sceneObject);
        return resolved ? new SceneObjectHandleImpl(resolved, this._traceScene) : null;
    }

    // ── Properties ────────────────────────────────────────

    getProp(name: string): string | number | boolean | Vec3 | undefined {
        const props = this._sceneObject.getBehavior(Properties);
        return props?.getValue(name) as string | number | boolean | Vec3 | undefined;
    }

    // ── Behaviors ────────────────────────────────────────

    getBehavior<T extends BehaviorData>(type: string): BehaviorHandle<T> | null {
        const behaviors = this._sceneObject.getBehaviors();
        const match = behaviors.find(b => b.name === type);
        if (!match) return null;
        return {
            type: match.name,
            data: Object.freeze(structuredClone(match.getData())) as Readonly<T>,
        };
    }

    setBehavior(type: string, overrides: Record<string, unknown>): void {
        this._sceneObject.setBehavior(type, overrides).catch(err => {
            Logger.error(`[Script] setBehavior error:`, err);
        });
    }

    // ── Actions (imperative — call behavior methods) ──────

    playAnimation(clip: string, opts?: { speed?: number; loop?: boolean }): void {
        const anim = this._sceneObject.getBehavior(Animation);
        if (anim) {
            void anim.play({ clip, speed: opts?.speed, loop: opts?.loop });
        }
    }

    stopAnimation(): void {
        const anim = this._sceneObject.getBehavior(Animation);
        if (anim) {
            anim.stop();
        }
    }

    playNote(note: number, opts?: { duration?: number; volume?: number }): void {
        const synth = this._sceneObject.getBehavior(Synth);
        if (synth) {
            synth.playNote(note, opts);
        } else {
            this._autoAddSynthAndPlay('playNote', note, opts);
        }
    }

    playFrequency(freq: number, opts?: { duration?: number; volume?: number }): void {
        const synth = this._sceneObject.getBehavior(Synth);
        if (synth) {
            synth.playFrequency(freq, opts);
        } else {
            this._autoAddSynthAndPlay('playFrequency', freq, opts);
        }
    }

    stopSynth(): void {
        const synth = this._sceneObject.getBehavior(Synth);
        if (synth) {
            synth.stop();
        }
    }

    setColor(color: string, materialIndex?: number): void {
        const renderer = this._sceneObject.getBehavior(Renderer);
        if (renderer) {
            const data = renderer.getData();
            const idx = materialIndex ?? 0;
            if (!data.materials) data.materials = [];
            while (data.materials.length <= idx) {
                data.materials.push({});
            }
            data.materials[idx].baseColorFactor = color;
            void renderer.commit(data);
        }
    }

    private _autoAddSynthAndPlay(
        method: 'playNote' | 'playFrequency',
        value: number,
        opts?: { duration?: number; volume?: number },
    ): void {
        this._sceneObject.addBehavior('Synth', {}).then(() => {
            const synth = this._sceneObject.getBehavior(Synth);
            if (synth) {
                synth[method](value, opts);
            }
        }).catch(err => {
            Logger.error(`[Script] Failed to auto-add Synth behavior:`, err);
        });
    }

    // ── Events ───────────────────────────────────────────

    dispatch(eventName: string): void {
        // Fire on all ScriptBehaviors (skip caller to prevent self-firing loops).
        // TODO: When event/send + event/receive graph blocks are implemented,
        // also fire custom events into BehaviorGraph on this object.
        for (const behavior of this._sceneObject.getBehaviors()) {
            if (isScriptBehavior(behavior) && behavior.name !== this._callerType) {
                (behavior as ScriptBehavior).fireEvent(eventName).catch(err => {
                    Logger.error(`[Script] dispatch to ScriptBehavior error:`, err);
                });
            }
        }
    }
}

// ── SceneHandle implementation ───────────────────────────

class SceneHandleImpl implements SceneHandle {
    constructor(private _traceScene: TraceScene) {}

    find(pathOrName: string): SceneObjectHandle | null {
        const obj = this._traceScene.resolveSceneObject(pathOrName);
        return obj ? new SceneObjectHandleImpl(obj, this._traceScene) : null;
    }

    get isPlaying(): boolean {
        return this._traceScene.isPlayMode();
    }

    get cameraPosition(): Vec3 {
        const cam = this._traceScene.getCamera();
        if (!cam) return { x: 0, y: 0, z: 0 };
        return { x: cam.position.x, y: cam.position.y, z: cam.position.z };
    }

    get cameraForward(): Vec3 {
        const cam = this._traceScene.getCamera();
        if (!cam) return { x: 0, y: 0, z: -1 };
        const fwd = cam.getForwardRay().direction;
        return { x: fwd.x, y: fwd.y, z: fwd.z };
    }
}

// ── Helper to detect ScriptBehavior without circular import ──

/**
 * Duck-type check for ScriptBehavior instances.
 * Uses the fireEvent method presence to avoid importing ScriptBehavior directly,
 * which would create a circular dependency.
 */
function isScriptBehavior(behavior: unknown): boolean {
    return (
        behavior !== null &&
        typeof behavior === 'object' &&
        'fireEvent' in (behavior as Record<string, unknown>) &&
        typeof (behavior as Record<string, unknown>).fireEvent === 'function'
    );
}

// ── Factory ──────────────────────────────────────────────

/**
 * Create a ScriptContext for a lifecycle hook invocation.
 *
 * @param sceneObject - The SceneObject this script is attached to
 * @param traceScene - The TraceScene for scene-level queries
 * @param behaviorData - The behavior's current data (shallow-copied; typed as Readonly<TData> in the script API)
 * @param time - Frame timing info
 * @param scriptName - Script @type name (for log prefix)
 */
export function createScriptContext(
    sceneObject: SceneObject,
    traceScene: TraceScene,
    behaviorData: BehaviorData,
    time: { deltaTime: number; elapsed: number },
    scriptName: string,
): ScriptContext {
    return {
        data: { ...behaviorData },
        self: new SceneObjectHandleImpl(sceneObject, traceScene, scriptName),
        scene: new SceneHandleImpl(traceScene),
        time: Object.freeze(time),
        log: {
            info: (...args: unknown[]) => console.log(`[Script:${scriptName}]`, ...args),
            warn: (...args: unknown[]) => console.warn(`[Script:${scriptName}]`, ...args),
            error: (...args: unknown[]) => console.error(`[Script:${scriptName}]`, ...args),
        },
    };
}
